import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";

/**
 * Build Cash Receipt Verification Subject
 *
 * Adapter that assembles the generic `verify_step_output` inputs for one bank
 * statement ingested by `ingest_bank_transactions_from_document`: the rows the
 * extraction read, with per-kind counts and sums computed in code, as the
 * `subject`; the statement itself as `documentIds`, so the verifier reads the
 * original (a CSV is inlined as text, a PDF or image attached); and the
 * document as the entity a failed check's review task links to.
 *
 * The check it feeds is completeness. An extractor can drop a line from a long
 * statement and pad the array with an empty row, and nothing downstream
 * notices: the receipt is never created, so there is nothing to match. The
 * computed sums let the verifier compare the extraction with the statement's
 * own control totals (a BAI file's "Total ACH Credits") without adding up the
 * amounts itself.
 *
 * Wire it from `extract_document_content_v2.documents`, beside ingest rather
 * than after it. Ingest fans out per receipt, and a fanout's children run every
 * step downstream of its source, so a check wired off ingest would run once per
 * receipt instead of once per statement.
 */
export const buildCashReceiptVerificationSubjectStep = defineStepConfig({
  type: "build_cash_receipt_verification_subject",
  name: "Build Cash Receipt Verification Subject",
  description:
    "Assemble the verify_step_output inputs (subject + documentIds + entity link) for a bank statement's extracted transactions.",
  category: "Data Access",
  libraryModule: "AccountsReceivable",
  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "The extracted bank statement (extract_document_content_v2.documents, fanned out per document)",
    ),
  }),
  outputSchema: z.object({
    subject: z
      .record(z.string(), z.unknown())
      .describe(
        "The extracted rows plus computed counts and sums per kind, to check against the statement",
      ),
    documentIds: z
      .array(z.uuid())
      .describe(
        "The statement's document id, for grounding; fed to verify_step_output.documentIds",
      ),
    sourceEntityType: z
      .nativeEnum(SystemEntity)
      .describe("Entity type the escalation task links to (CORE_DOCUMENT)"),
    sourceEntityId: z
      .uuid()
      .describe("Entity id the escalation task links to (the document id)"),
  }),
  configSchema: z.object({}),
  emits: [] as const,
});
